/**
 * Tiny i18n module for the customer portal.
 *
 * No framework dependency. Just a `t()` function backed by a flat dictionary,
 * a `useT()` hook so components re-render on locale switch, and the `<html>`
 * `lang` + `dir` attributes get updated for RTL languages.
 *
 * Translation files live in /src/locales/<code>.ts. Each is a flat record of
 * key → string. Missing keys fall back to the English string (or the key
 * itself in dev) so we never crash on a partial translation.
 *
 * Persistence: chosen locale is stored in localStorage so it survives reloads.
 */
import { useEffect, useState } from 'react';
import { pageLocale } from './locale-path';
import { en } from '../locales/en';
import { zhCN } from '../locales/zh-CN';
import { es } from '../locales/es';
import { ar } from '../locales/ar';
import { fr } from '../locales/fr';
import { pt } from '../locales/pt';
import { hi } from '../locales/hi';
import { bn } from '../locales/bn';
import { ru } from '../locales/ru';
import { de } from '../locales/de';
import { ja } from '../locales/ja';
import { ur } from '../locales/ur';
import { fa } from '../locales/fa';
import { tr } from '../locales/tr';
import { ta } from '../locales/ta';
import { te } from '../locales/te';
import { id } from '../locales/id';
import { mr } from '../locales/mr';
import { vi } from '../locales/vi';
import { ko } from '../locales/ko';
import { it } from '../locales/it';
import { th } from '../locales/th';
import { gu } from '../locales/gu';
import { pl } from '../locales/pl';
import { uk } from '../locales/uk';
import { ms } from '../locales/ms';
import { nl } from '../locales/nl';
import { ro } from '../locales/ro';
import { zhTW } from '../locales/zh-TW';

export const LOCALES = {
  'en':    { name: 'English',  flag: '🇺🇸', dict: en,   dir: 'ltr' as const },
  'zh-CN': { name: '中文',      flag: '🇨🇳', dict: zhCN, dir: 'ltr' as const },
  'hi':    { name: 'हिन्दी',      flag: '🇮🇳', dict: hi,   dir: 'ltr' as const },
  'es':    { name: 'Español',   flag: '🇪🇸', dict: es,   dir: 'ltr' as const },
  'ar':    { name: 'العربية',   flag: '🇸🇦', dict: ar,   dir: 'rtl' as const },
  'bn':    { name: 'বাংলা',     flag: '🇧🇩', dict: bn,   dir: 'ltr' as const },
  'pt':    { name: 'Português', flag: '🇵🇹', dict: pt,   dir: 'ltr' as const },
  'ru':    { name: 'Русский',   flag: '🇷🇺', dict: ru,   dir: 'ltr' as const },
  'ur':    { name: 'اردو',       flag: '🇵🇰', dict: ur,   dir: 'rtl' as const },
  'fr':    { name: 'Français',  flag: '🇫🇷', dict: fr,   dir: 'ltr' as const },
  'de':    { name: 'Deutsch',   flag: '🇩🇪', dict: de,   dir: 'ltr' as const },
  'ja':    { name: '日本語',     flag: '🇯🇵', dict: ja,   dir: 'ltr' as const },
  'tr':    { name: 'Türkçe',    flag: '🇹🇷', dict: tr,   dir: 'ltr' as const },
  'ta':    { name: 'தமிழ்',      flag: '🇮🇳', dict: ta,   dir: 'ltr' as const },
  'te':    { name: 'తెలుగు',     flag: '🇮🇳', dict: te,   dir: 'ltr' as const },
  'fa':    { name: 'فارسی',     flag: '🇮🇷', dict: fa,   dir: 'rtl' as const },
  'id':    { name: 'Bahasa Indonesia', flag: '🇮🇩', dict: id, dir: 'ltr' as const },
  'mr':    { name: 'मराठी',      flag: '🇮🇳', dict: mr,   dir: 'ltr' as const },
  'vi':    { name: 'Tiếng Việt', flag: '🇻🇳', dict: vi,   dir: 'ltr' as const },
  'ko':    { name: '한국어',      flag: '🇰🇷', dict: ko,   dir: 'ltr' as const },
  'it':    { name: 'Italiano',  flag: '🇮🇹', dict: it,   dir: 'ltr' as const },
  'th':    { name: 'ไทย',        flag: '🇹🇭', dict: th,   dir: 'ltr' as const },
  'gu':    { name: 'ગુજરાતી',     flag: '🇮🇳', dict: gu,   dir: 'ltr' as const },
  'pl':    { name: 'Polski',    flag: '🇵🇱', dict: pl,   dir: 'ltr' as const },
  'uk':    { name: 'Українська', flag: '🇺🇦', dict: uk,   dir: 'ltr' as const },
  'ms':    { name: 'Bahasa Melayu', flag: '🇲🇾', dict: ms, dir: 'ltr' as const },
  'nl':    { name: 'Nederlands', flag: '🇳🇱', dict: nl,   dir: 'ltr' as const },
  'ro':    { name: 'Română',    flag: '🇷🇴', dict: ro,   dir: 'ltr' as const },
  'zh-TW': { name: '繁體中文',    flag: '🇹🇼', dict: zhTW, dir: 'ltr' as const },
};

export type LocaleCode = keyof typeof LOCALES;

const STORAGE_KEY = 'kardolive.locale';

const subscribers = new Set<() => void>();
const pageMessages: Partial<Record<LocaleCode, Record<string, string>>> = {};
export function registerPageMessages(code: LocaleCode, messages: Record<string, string>) {
  pageMessages[code] = messages;
  if (code === _locale) subscribers.forEach(fn => fn());
}
export function hasPageMessage(source: string): boolean {
  return _locale === 'en' || Object.hasOwn(pageMessages[_locale] ?? {}, source);
}
export function pageMessage(source: string): string {
  const messages = pageMessages[_locale];
  return messages && Object.hasOwn(messages, source) ? messages[source] : source;
}

let _locale: LocaleCode = (() => {
  const fromPage = typeof window !== 'undefined' ? pageLocale(window.location.pathname) : undefined;
  if (fromPage) return fromPage;
  let saved: string | null = null;
  try { saved = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null; } catch { /* Storage can be unavailable in embedded/private browsing. */ }
  if (saved && Object.prototype.hasOwnProperty.call(LOCALES, saved)) return saved as LocaleCode;
  // Fallback to browser language (best match)
  const nav = (typeof navigator !== 'undefined' && navigator.language) || 'en';
  // Traditional Chinese variants — must be checked BEFORE the generic zh prefix
  if (nav === 'zh-TW' || nav === 'zh-Hant' || nav.startsWith('zh-Hant') || nav.startsWith('zh-TW') || nav.startsWith('zh-HK')) return 'zh-TW';
  if (nav.startsWith('zh')) return 'zh-CN';
  if (nav.startsWith('hi')) return 'hi';
  if (nav.startsWith('bn')) return 'bn';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('ar')) return 'ar';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('pt')) return 'pt';
  if (nav.startsWith('ru')) return 'ru';
  if (nav.startsWith('de')) return 'de';
  if (nav.startsWith('ja')) return 'ja';
  if (nav.startsWith('ur')) return 'ur';
  if (nav.startsWith('fa')) return 'fa';
  if (nav.startsWith('tr')) return 'tr';
  if (nav.startsWith('ta')) return 'ta';
  if (nav.startsWith('te')) return 'te';
  if (nav.startsWith('id')) return 'id';
  if (nav.startsWith('mr')) return 'mr';
  if (nav.startsWith('vi')) return 'vi';
  if (nav.startsWith('ko')) return 'ko';
  if (nav.startsWith('it')) return 'it';
  if (nav.startsWith('th')) return 'th';
  if (nav.startsWith('gu')) return 'gu';
  if (nav.startsWith('pl')) return 'pl';
  if (nav.startsWith('uk')) return 'uk';
  if (nav.startsWith('ms')) return 'ms';
  if (nav.startsWith('nl')) return 'nl';
  if (nav.startsWith('ro')) return 'ro';
  return 'en';
})();

/** Portal bootstrap owns document attributes; importing shared help must not restyle Meet. */
export function initializePageLanguage() { applyHtmlAttrs(_locale); }

export function getLocale(): LocaleCode {
  return _locale;
}

export function setLocale(loc: LocaleCode) {
  if (!Object.prototype.hasOwnProperty.call(LOCALES, loc)) return;
  _locale = loc;
  try { localStorage.setItem(STORAGE_KEY, loc); } catch { /* ignore */ }
  applyHtmlAttrs(loc);
  subscribers.forEach((fn) => fn());
}

function applyHtmlAttrs(loc: LocaleCode) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('lang', loc);
  document.documentElement.setAttribute('dir', LOCALES[loc].dir);
}

/**
 * Translate a key. If `params` are passed, replace `{name}` placeholders.
 * Falls back to English then the key itself.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const dict = LOCALES[_locale].dict as Record<string, string>;
  const enDict = en as Record<string, string>;
  let s = dict[key] ?? pageMessages[_locale]?.[enDict[key]] ?? enDict[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.split(`{${k}}`).join(String(v));
    }
  }
  return s;
}

/** React hook: returns the current locale + a translate function. */
export function useT() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    subscribers.add(fn);
    return () => { subscribers.delete(fn); };
  }, []);
  return { t, locale: _locale, setLocale, locales: LOCALES };
}
